import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  IconButton,
  Paper,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Skeleton,
  InputAdornment,
  Collapse,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useAdminFetch } from '../hooks/useAdminFetch';

interface AuditLogEntry {
  id: number;
  timestamp: string;
  action: string;
  action_display: string;
  category: string;
  category_display: string;
  user_id: number | null;
  user_email: string | null;
  user_username: string | null;
  is_authenticated: boolean;
  model_name: string | null;
  object_id: number | null;
  object_repr: string | null;
  ip_address: string | null;
  description: string | null;
  has_changes: boolean;
}

interface AuditLogDetail extends AuditLogEntry {
  old_values: Record<string, unknown> | null;
  new_values: Record<string, unknown> | null;
  user_agent: string | null;
  request_path: string | null;
  request_method: string | null;
  extra_data: Record<string, unknown> | null;
}

interface FilterOption {
  value: string;
  label: string;
}

interface FiltersResponse {
  actions: FilterOption[];
  categories: FilterOption[];
  models: string[];
}

interface StatsResponse {
  today_total: number;
  security_events_week: number;
  failed_logins_week: number;
  category_breakdown: Array<{ category: string; count: number }>;
  top_actions: Array<{ action: string; count: number }>;
  recent_security: Array<{
    id: number;
    timestamp: string;
    action: string;
    user_email: string | null;
    ip_address: string | null;
    description: string | null;
  }>;
}

const categoryColors: Record<string, 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success'> = {
  model: 'primary',
  auth: 'info',
  admin: 'warning',
  security: 'error',
};

const AdminAuditTrails: React.FC = () => {
  const navigate = useNavigate();
  const adminFetch = useAdminFetch();
  const apiUrl = process.env.REACT_APP_API_URL;

  // Data states
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState<StatsResponse | null>(null);
  const [filters, setFilters] = useState<FiltersResponse | null>(null);
  const [selectedLog, setSelectedLog] = useState<AuditLogDetail | null>(null);

  // UI states
  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Filter values
  const [searchQuery, setSearchQuery] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [modelFilter, setModelFilter] = useState('');
  const [authFilter, setAuthFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const buildQueryParams = useCallback(() => {
    const params = new URLSearchParams();
    params.set('page', (page + 1).toString());
    params.set('per_page', rowsPerPage.toString());

    if (searchQuery) params.set('search', searchQuery);
    if (actionFilter) params.set('action', actionFilter);
    if (categoryFilter) params.set('category', categoryFilter);
    if (modelFilter) params.set('model', modelFilter);
    if (authFilter) params.set('is_authenticated', authFilter);
    if (startDate) params.set('start_date', new Date(startDate).toISOString());
    if (endDate) params.set('end_date', new Date(endDate).toISOString());

    return params.toString();
  }, [page, rowsPerPage, searchQuery, actionFilter, categoryFilter, modelFilter, authFilter, startDate, endDate]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const queryString = buildQueryParams();
      const response = await adminFetch(`${apiUrl}/api/admin/audit-logs/?${queryString}`);
      setLogs(response.logs || []);
      setTotal(response.total || 0);
    } catch (err) {
      console.error('Failed to fetch audit logs:', err);
      setError('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [adminFetch, apiUrl, buildQueryParams]);

  const fetchFilters = useCallback(async () => {
    try {
      const response = await adminFetch(`${apiUrl}/api/admin/audit-logs/filters/`);
      setFilters(response);
    } catch (err) {
      console.error('Failed to fetch filters:', err);
    }
  }, [adminFetch, apiUrl]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await adminFetch(`${apiUrl}/api/admin/audit-logs/stats/`);
      setStats(response);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, [adminFetch, apiUrl]);

  const fetchLogDetail = async (logId: number) => {
    setLoadingDetail(true);
    try {
      const response = await adminFetch(`${apiUrl}/api/admin/audit-logs/${logId}/`);
      setSelectedLog(response);
      setDetailDialogOpen(true);
    } catch (err) {
      console.error('Failed to fetch log detail:', err);
    } finally {
      setLoadingDetail(false);
    }
  };

  useEffect(() => {
    fetchFilters();
    fetchStats();
  }, [fetchFilters, fetchStats]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleRefresh = () => {
    fetchLogs();
    fetchStats();
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
    setPage(0);
    fetchLogs();
  };

  const clearFilters = () => {
    setSearchQuery('');
    setActionFilter('');
    setCategoryFilter('');
    setModelFilter('');
    setAuthFilter('');
    setStartDate('');
    setEndDate('');
    setPage(0);
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const renderChanges = (oldValues: Record<string, unknown> | null, newValues: Record<string, unknown> | null) => {
    if (!oldValues && !newValues) {
      return <Typography color="text.secondary">No changes recorded</Typography>;
    }

    const allKeys = new Set([
      ...Object.keys(oldValues || {}),
      ...Object.keys(newValues || {}),
    ]);

    return (
      <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Field</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Old Value</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>New Value</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.from(allKeys).map((key) => {
              const oldVal = oldValues?.[key];
              const newVal = newValues?.[key];
              const changed = JSON.stringify(oldVal) !== JSON.stringify(newVal);

              return (
                <TableRow key={key} sx={changed ? { backgroundColor: 'action.hover' } : {}}>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{key}</TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'monospace',
                        color: changed ? 'error.main' : 'text.secondary',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-all',
                      }}
                    >
                      {oldVal === undefined ? '-' : JSON.stringify(oldVal, null, 2)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'monospace',
                        color: changed ? 'success.main' : 'text.secondary',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-all',
                      }}
                    >
                      {newVal === undefined ? '-' : JSON.stringify(newVal, null, 2)}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 5, mb: 5 }}>
      <Box className="page-header">
        <Box className="title-row" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={() => navigate('/admin')} aria-label="back to admin">
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" component="h1" className="page-title">
              Audit Trails
            </Typography>
          </Box>
          <IconButton onClick={handleRefresh} aria-label="refresh data">
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" color="primary">{stats.today_total}</Typography>
              <Typography variant="body2" color="text.secondary">Events Today</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" color="error">{stats.security_events_week}</Typography>
              <Typography variant="body2" color="text.secondary">Security Events (7d)</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" color="warning.main">{stats.failed_logins_week}</Typography>
              <Typography variant="body2" color="text.secondary">Failed Logins (7d)</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4">{total}</Typography>
              <Typography variant="body2" color="text.secondary">Total Events</Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box component="form" onSubmit={handleSearch} sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            size="small"
            placeholder="Search by email, username, or description..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ flexGrow: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Button type="submit" variant="contained">
            Search
          </Button>
          <Button
            variant="outlined"
            startIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            onClick={() => setShowFilters(!showFilters)}
          >
            <FilterListIcon />
          </Button>
        </Box>

        <Collapse in={showFilters}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select
                  value={categoryFilter}
                  label="Category"
                  onChange={(e) => { setCategoryFilter(e.target.value); setPage(0); }}
                >
                  <MenuItem value="">All</MenuItem>
                  {filters?.categories.map((cat) => (
                    <MenuItem key={cat.value} value={cat.value}>{cat.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Action</InputLabel>
                <Select
                  value={actionFilter}
                  label="Action"
                  onChange={(e) => { setActionFilter(e.target.value); setPage(0); }}
                >
                  <MenuItem value="">All</MenuItem>
                  {filters?.actions.map((act) => (
                    <MenuItem key={act.value} value={act.value}>{act.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Model</InputLabel>
                <Select
                  value={modelFilter}
                  label="Model"
                  onChange={(e) => { setModelFilter(e.target.value); setPage(0); }}
                >
                  <MenuItem value="">All</MenuItem>
                  {filters?.models.map((model) => (
                    <MenuItem key={model} value={model}>{model}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Authenticated</InputLabel>
                <Select
                  value={authFilter}
                  label="Authenticated"
                  onChange={(e) => { setAuthFilter(e.target.value); setPage(0); }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Logged In</MenuItem>
                  <MenuItem value="false">Anonymous</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                size="small"
                label="Start Date"
                type="datetime-local"
                value={startDate}
                onChange={(e) => { setStartDate(e.target.value); setPage(0); }}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                size="small"
                label="End Date"
                type="datetime-local"
                value={endDate}
                onChange={(e) => { setEndDate(e.target.value); setPage(0); }}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button variant="outlined" onClick={clearFilters} fullWidth>
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </Collapse>
      </Paper>

      {/* Logs Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Timestamp</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Target</TableCell>
                <TableCell>IP Address</TableCell>
                <TableCell align="center">Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 10 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                  </TableRow>
                ))
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="text.secondary" sx={{ py: 4 }}>
                      No audit logs found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => (
                  <TableRow key={log.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {formatTimestamp(log.timestamp)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={log.category_display}
                        size="small"
                        color={categoryColors[log.category] || 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{log.action_display}</Typography>
                    </TableCell>
                    <TableCell>
                      {log.user_email || log.user_username || (
                        <Typography color="text.secondary" variant="body2">
                          Anonymous
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {log.object_repr || log.model_name || '-'}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                      {log.ip_address || '-'}
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => fetchLogDetail(log.id)}
                        disabled={loadingDetail}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 20, 50]}
        />
      </Paper>

      {/* Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedLog && (
          <>
            <DialogTitle>
              Audit Log Details
              <Typography variant="body2" color="text.secondary">
                ID: {selectedLog.id}
              </Typography>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Timestamp</Typography>
                  <Typography>{formatTimestamp(selectedLog.timestamp)}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Category</Typography>
                  <Box>
                    <Chip
                      label={selectedLog.category_display}
                      size="small"
                      color={categoryColors[selectedLog.category] || 'default'}
                    />
                  </Box>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Action</Typography>
                  <Typography>{selectedLog.action_display}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Authenticated</Typography>
                  <Typography>{selectedLog.is_authenticated ? 'Yes' : 'No'}</Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">User</Typography>
                  <Typography>
                    {selectedLog.user_email || selectedLog.user_username || 'Anonymous'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">IP Address</Typography>
                  <Typography sx={{ fontFamily: 'monospace' }}>
                    {selectedLog.ip_address || '-'}
                  </Typography>
                </Grid>

                {selectedLog.model_name && (
                  <>
                    <Grid item xs={6} md={4}>
                      <Typography variant="caption" color="text.secondary">Model</Typography>
                      <Typography>{selectedLog.model_name}</Typography>
                    </Grid>
                    <Grid item xs={6} md={4}>
                      <Typography variant="caption" color="text.secondary">Object ID</Typography>
                      <Typography>{selectedLog.object_id || '-'}</Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="caption" color="text.secondary">Object</Typography>
                      <Typography>{selectedLog.object_repr || '-'}</Typography>
                    </Grid>
                  </>
                )}

                {selectedLog.request_path && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Request</Typography>
                    <Typography sx={{ fontFamily: 'monospace' }}>
                      {selectedLog.request_method} {selectedLog.request_path}
                    </Typography>
                  </Grid>
                )}

                {selectedLog.description && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Description</Typography>
                    <Typography>{selectedLog.description}</Typography>
                  </Grid>
                )}

                {selectedLog.user_agent && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">User Agent</Typography>
                    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                      {selectedLog.user_agent}
                    </Typography>
                  </Grid>
                )}

                {(selectedLog.old_values || selectedLog.new_values) && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                      Changes
                    </Typography>
                    {renderChanges(selectedLog.old_values, selectedLog.new_values)}
                  </Grid>
                )}

                {selectedLog.extra_data && Object.keys(selectedLog.extra_data).length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                      Extra Data
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography
                        component="pre"
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.875rem',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-all',
                          m: 0,
                        }}
                      >
                        {JSON.stringify(selectedLog.extra_data, null, 2)}
                      </Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Container>
  );
};

export default AdminAuditTrails;
